import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const LOCAL_VERSION_KEY = 'app_version';

const VersionCheck = () => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [newVersion, setNewVersion] = useState(null);

  useEffect(() => {
    // Initial check
    checkVersion();
    
    // Set up periodic checks
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    
    return () => clearInterval(interval);
  }, []);

  const checkVersion = async () => {
    try {
      const response = await api.getAppVersion();
      const serverVersion = response.data?.version;
      
      if (!serverVersion) return;
      
      const localVersion = localStorage.getItem(LOCAL_VERSION_KEY);
      
      if (!localVersion) {
        // First time - save current version
        localStorage.setItem(LOCAL_VERSION_KEY, serverVersion);
      } else if (localVersion !== serverVersion) {
        // Version mismatch - show update banner
        setNewVersion(serverVersion);
        setShowUpdateBanner(true);
      }
    } catch (error) {
      // Silently fail - version check is not critical
      console.debug('Version check failed:', error);
    }
  };

  const handleRefresh = () => {
    // Save new version before refresh
    if (newVersion) {
      localStorage.setItem(LOCAL_VERSION_KEY, newVersion);
    }
    // Force hard refresh to bypass cache
    window.location.reload(true);
  };

  const handleDismiss = () => {
    setShowUpdateBanner(false);
    // Save version to prevent showing again until next update
    if (newVersion) {
      localStorage.setItem(LOCAL_VERSION_KEY, newVersion);
    }
  };

  if (!showUpdateBanner) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
      <div className="bg-indigo-600 text-white rounded-lg shadow-lg p-4 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-indigo-500 rounded-full">
            <RefreshCw size={20} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold">New Version Available!</h4>
            <p className="text-sm text-indigo-100 mt-1">
              A new version of the app has been deployed. Please refresh to get the latest features and fixes.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleRefresh}
                className="px-4 py-1.5 bg-white text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition"
              >
                Refresh Now
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-1.5 text-indigo-100 text-sm hover:text-white transition"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionCheck;
